import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { tzLabel } from "../lib/helpers";

const MONTHS_SHORT = ["", "janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

const parseDate = (d) => {
  try {
    const [y, mo, day] = String(d).slice(0, 10).split("-").map(Number);
    return { day, mo, y };
  } catch {
    return null;
  }
};

const relativeLabel = (startUtc) => {
  const start = new Date(startUtc);
  const diffDays = Math.round((new Date(start.getFullYear(), start.getMonth(), start.getDate()) - new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())) / 86400000);
  if (diffDays === 0) return "Aujourd'hui";
  if (diffDays === 1) return "Demain";
  if (diffDays > 1 && diffDays < 7) return `Dans ${diffDays} jours`;
  return null;
};

// Mini-agenda des prochains RDV (visio + physiques). projectPath / missionPath: fn(item) => route|null.
export const RdvAgenda = ({ items = [], showClient = false, projectPath, missionPath }) => {
  const ppath = projectPath || ((v) => `/projets/${v.project_id}?vue=etapes`);
  const mpath = missionPath || ((v) => `/missions/${v.mission_id}`);
  const target = (v) => (v.source === "mission" ? mpath(v) : ppath(v));
  return (
    <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-6" data-testid="visio-agenda">
      <div className="flex items-baseline justify-between mb-1">
        <h2 className="font-display text-xl text-marine">Prochains rendez-vous</h2>
        {items.length > 0 && <span className="text-xs text-marine/80">{items.length} à venir</span>}
      </div>
      <p className="text-xs text-marine/80 mb-4">RDV visio et physiques programmés</p>
      {items.length === 0 ? (
        <p className="text-sm text-marine/80" data-testid="visio-agenda-empty">Vous n'avez aucun rendez-vous à venir.</p>
      ) : (
        <div className="divide-y divide-marine/10">
          {items.map((v, i) => {
            const d = parseDate(v.rdv_date);
            const rel = relativeLabel(v.start_utc);
            const isVisio = v.rdv_type === "visio";
            const context = v.source === "mission" ? (v.mission_label || "Mission") : v.project_name;
            const maps = v.rdv_address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(v.rdv_address)}` : null;
            const to = target(v);
            const titleInner = (
              <>
                <span className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${isVisio ? "bg-marine-50 text-marine border-marine/30" : "bg-bordeaux-50 text-bordeaux border-bordeaux/30"}`}>
                  {isVisio ? "Visio" : "Physique"}
                </span>
                {v.phase_name}
              </>
            );
            return (
              <div key={i} data-testid={`visio-agenda-item-${i}`} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
                <div className="w-14 shrink-0 text-center">
                  <div className="font-display text-2xl leading-none text-marine">{d ? String(d.day).padStart(2, "0") : "—"}</div>
                  <div className="text-xs uppercase tracking-wider text-marine/80 mt-0.5">{d ? MONTHS_SHORT[d.mo] : ""}</div>
                </div>
                <div className="min-w-0 flex-1">
                  {to ? (
                    <Link to={to} className="text-sm font-medium text-marine hover:text-marine transition-colors truncate flex items-center gap-1.5" data-testid={`visio-agenda-link-${i}`}>{titleInner}</Link>
                  ) : (
                    <div className="text-sm font-medium text-marine truncate flex items-center gap-1.5">{titleInner}</div>
                  )}
                  <div className="text-xs text-marine/80 truncate">
                    {context}{showClient && v.client_name ? ` · ${v.client_name}` : ""}
                  </div>
                  <div className="text-xs text-marine mt-0.5 truncate">
                    {rel && <span className="font-medium">{rel} · </span>}
                    {v.rdv_time}{v.rdv_timezone ? ` (${tzLabel(v.rdv_timezone)})` : ""}
                    {!isVisio && v.rdv_address ? ` · ${v.rdv_address}` : ""}
                  </div>
                </div>
                {isVisio && v.rdv_link && (
                  <a href={v.rdv_link} target="_blank" rel="noopener noreferrer" data-testid={`visio-agenda-join-${i}`}
                    className="shrink-0 inline-flex items-center gap-1.5 border border-marine/25 text-marine rounded-sm px-3 py-1.5 text-xs hover:bg-marine hover:text-white transition-colors">
                    <Icons.Video size={16} /> Rejoindre
                  </a>
                )}
                {!isVisio && maps && (
                  <a href={maps} target="_blank" rel="noopener noreferrer" data-testid={`visio-agenda-map-${i}`}
                    className="shrink-0 inline-flex items-center gap-1.5 border border-marine/25 text-marine rounded-sm px-3 py-1.5 text-xs hover:bg-marine hover:text-white transition-colors">
                    <Icons.MapPin size={16} /> Carte
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RdvAgenda;
